import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import Barcode from "react-barcode";
import {
  INVENTORY_DIFF_REASON_OPTIONS,
  inventoryDiffReasonLabel,
  inventoryRevisionStatusLabel,
} from "../shared/inventoryRevisionEnums";
import {
  computePostedRevisionFinance,
  getPostedRevisionFinanceRows,
  moneyRu,
} from "../shared/inventoryRevisionPostedFinance";
import { getAccessToken } from "../shared/tokenStorage";
import ConfirmModal from "../components/ConfirmModal";
import {
  fetchInventoryRevisionDetail,
  fetchInventoryRevisionItems,
  fetchProductByBarcode,
  fetchRevisionItemsByBarcode,
  createInventoryRevisionItem,
  patchInventoryRevisionItem,
  selectProductByBarcode,
  selectProductErrorByBarcode,
  selectProductStatusByBarcode,
  selectRevisionItemsByBarcode,
  selectRevisionItemsErrorByBarcode,
  selectRevisionItemsStatusByBarcode,
  cancelInventoryRevision,
  selectCancelRevisionErrorById,
  selectCancelRevisionStatusById,
  selectStartRevisionErrorById,
  selectStartRevisionStatusById,
  publishInventoryRevision,
  selectPublishRevisionErrorById,
  selectPublishRevisionStatusById,
  selectToApprovedRevisionErrorById,
  selectToApprovedRevisionStatusById,
  selectToCountedRevisionErrorById,
  selectToCountedRevisionStatusById,
  startInventoryRevision,
  toApprovedInventoryRevision,
  toCountedInventoryRevision,
  selectItemsByRevisionId,
  selectItemsErrorByRevisionId,
  selectItemsStatusByRevisionId,
  selectRevisionById,
  selectRevisionErrorById,
  selectRevisionStatusById,
} from "../features/inventoryRevisions/inventoryRevisionsSlice";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

async function downloadRevisionExport(url, filename) {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${url}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let msg = "Ошибка экспорта";
    try {
      const data = await res.json();
      if (data?.detail) msg = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const a = document.createElement("a");
  const href = URL.createObjectURL(blob);
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function formatUser(value) {
  if (!value) return "—";
  if (typeof value === "number" || typeof value === "string") return `#${value}`;
  if (typeof value === "object") {
    const username = value.username ? `@${value.username}` : null;
    const fullName = [value.first_name, value.last_name].filter(Boolean).join(" ").trim() || null;
    const id = value.id != null ? `#${value.id}` : null;
    return [fullName, username, id].filter(Boolean).join(" ") || "—";
  }
  return "—";
}

function parseQty(value) {
  if (value === null || typeof value === "undefined") return null;
  const v = typeof value === "string" ? value.trim() : value;
  if (v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n;
}

function formatQty(value) {
  const n = parseQty(value);
  if (n === null) return "—";
  // Show up to 3 decimals but trim trailing zeros: 1.000 -> 1, 1.200 -> 1.2
  return n.toFixed(3).replace(/\.?0+$/, "");
}

function formatSignedQty(value) {
  const n = parseQty(value);
  if (n === null) return "—";
  const txt = formatQty(n);
  return n > 0 ? `+${txt}` : txt;
}

/** Display difference: actual (counted) − expected. Negative when actual < expected, positive when actual > expected. */
function getDisplayDiff(expectedQty, countedQty, backendDiffQty) {
  const expected = parseQty(expectedQty);
  const counted = parseQty(countedQty);
  if (counted !== null && expected !== null) return counted - expected;
  return parseQty(backendDiffQty);
}

function BarcodeBlock({ barcode }) {
  const text = String(barcode || "—").trim();
  const canRender = text && text !== "—";
  return (
    <div style={styles.barcodeBlock} aria-label={`barcode ${text}`}>
      <div style={styles.barcodeVisual}>
        {canRender ? (
          <Barcode
            value={text}
            format="CODE128"
            displayValue={false}
            width={1.35}
            height={44}
            margin={0}
            background="transparent"
            lineColor="var(--c-text)"
          />
        ) : (
          <div style={styles.barcodePlaceholder} />
        )}
      </div>
      <div style={styles.barcodeText}>{text}</div>
    </div>
  );
}

function parseCountedInt(value) {
  if (value === null || typeof value === "undefined") return null;
  const v = typeof value === "string" ? value.trim() : value;
  if (v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  if (round3(n) !== round3(i)) return null;
  return i;
}

function formatInt(value) {
  const n = parseCountedInt(value);
  if (n === null) return "—";
  return String(n);
}

function round3(n) {
  return Math.round(Number(n) * 1000) / 1000;
}

function isDiffReasonRequired(expectedQty, countedQty, diffQty) {
  const e = parseQty(expectedQty);
  const c = parseQty(countedQty);
  if (e !== null && c !== null) return round3(e) !== round3(c);

  const d = parseQty(diffQty);
  if (d !== null) return round3(d) !== 0;

  return false;
}

export default function WarehouseRevisionDetailPage() {
  const { revisionId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : null;
  }, [location.state]);

  const revision = useSelector(selectRevisionById(revisionId));
  const revStatus = useSelector(selectRevisionStatusById(revisionId));
  const revError = useSelector(selectRevisionErrorById(revisionId));
  const startStatus = useSelector(selectStartRevisionStatusById(revisionId));
  const startError = useSelector(selectStartRevisionErrorById(revisionId));
  const toCountedStatus = useSelector(selectToCountedRevisionStatusById(revisionId));
  const toCountedError = useSelector(selectToCountedRevisionErrorById(revisionId));
  const toApprovedStatus = useSelector(selectToApprovedRevisionStatusById(revisionId));
  const toApprovedError = useSelector(selectToApprovedRevisionErrorById(revisionId));
  const publishStatus = useSelector(selectPublishRevisionStatusById(revisionId));
  const publishError = useSelector(selectPublishRevisionErrorById(revisionId));
  const cancelStatus = useSelector(selectCancelRevisionStatusById(revisionId));
  const cancelError = useSelector(selectCancelRevisionErrorById(revisionId));
  const items = useSelector(selectItemsByRevisionId(revisionId));
  const itemsStatus = useSelector(selectItemsStatusByRevisionId(revisionId));
  const itemsError = useSelector(selectItemsErrorByRevisionId(revisionId));

  const [barcode, setBarcode] = useState("");
  const [productName, setProductName] = useState("");
  const [applyKey, setApplyKey] = useState(0);
  const [precheckError, setPrecheckError] = useState(null);
  const [precheckMissingById, setPrecheckMissingById] = useState(() => ({}));

  const [addProductOpen, setAddProductOpen] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [lastSearchedBarcode, setLastSearchedBarcode] = useState("");
  const [lookupMode, setLookupMode] = useState("idle"); // idle | revision_items | product
  const [lookupNotice, setLookupNotice] = useState(null);
  const [revItemEdits, setRevItemEdits] = useState(() => ({})); // by itemId

  const [editItemId, setEditItemId] = useState(null);
  const [editDraft, setEditDraft] = useState(null); // { counted_qty, diff_reason, diff_comment, saving, error, fieldErrors }
  const [productFromBarcodeDraft, setProductFromBarcodeDraft] = useState({
    counted_qty: "",
    diff_reason: "",
    diff_comment: "",
    saving: false,
    error: null,
    fieldErrors: {},
  });
  const [exportFormat, setExportFormat] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const lastEmptyBarcodeTriggered = useRef(null);
  const skipNextDebounceSearch = useRef(false);
  const clearFiltersAfterNextSuccess = useRef(false);

  const normalizedBarcode = String(barcodeInput || "").replace(/\s+/g, "").trim();
  const normalizedFilterBarcode = String(barcode || "").replace(/\s+/g, "").trim();
  const barcodeForLookup = normalizedBarcode || lastSearchedBarcode;
  const productLookup = useSelector(selectProductByBarcode(barcodeForLookup));
  const productLookupStatus = useSelector(selectProductStatusByBarcode(barcodeForLookup));
  const productLookupError = useSelector(selectProductErrorByBarcode(barcodeForLookup));

  const revisionItemsLookup = useSelector(selectRevisionItemsByBarcode(revisionId, barcodeForLookup));
  const revisionItemsLookupStatus = useSelector(selectRevisionItemsStatusByBarcode(revisionId, barcodeForLookup));
  const revisionItemsLookupError = useSelector(selectRevisionItemsErrorByBarcode(revisionId, barcodeForLookup));

  const financeRowSource = useMemo(() => getPostedRevisionFinanceRows(revision, items), [revision, items]);

  const postedFinance = useMemo(() => {
    if (!financeRowSource?.rows?.length) return null;
    return computePostedRevisionFinance(financeRowSource.rows);
  }, [financeRowSource]);

  const financeScopeNote =
    revision?.status === "posted" &&
    financeRowSource?.scope === "list" &&
    (String(barcode || "").trim() || String(productName || "").trim())
      ? "Сводка по текущему фильтру поиска (не по всей ревизии). Сбросьте фильтр или обновите страницу, чтобы увидеть полный итог."
      : null;

  const fetchRevision = useCallback(() => {
    dispatch(fetchInventoryRevisionDetail({ revisionId }));
  }, [dispatch, revisionId]);

  const fetchItems = useCallback(() => {
    dispatch(fetchInventoryRevisionItems({ revisionId, barcode, productName }));
  }, [barcode, dispatch, productName, revisionId]);

  const onExportExcel = useCallback(async () => {
    setExportError(null);
    setExportFormat("excel");
    try {
      await downloadRevisionExport(
        `/api/product/inventory-revisions/${revisionId}/export-excel/`,
        `inventory-revision-${revisionId}.xlsx`
      );
    } catch (e) {
      setExportError(e?.message || "Ошибка экспорта");
    } finally {
      setExportFormat(null);
    }
  }, [revisionId]);

  const onExportPdf = useCallback(async () => {
    setExportError(null);
    setExportFormat("pdf");
    try {
      await downloadRevisionExport(
        `/api/product/inventory-revisions/${revisionId}/export-pdf/`,
        `inventory-revision-${revisionId}.pdf`
      );
    } catch (e) {
      setExportError(e?.message || "Ошибка экспорта");
    } finally {
      setExportFormat(null);
    }
  }, [revisionId]);

  const onStartRevision = useCallback(async () => {
    try {
      await dispatch(startInventoryRevision({ revisionId })).unwrap();
      // starting revision recreates items -> refresh both
      fetchRevision();
      fetchItems();
    } catch {
      // error is in redux
    }
  }, [dispatch, fetchItems, fetchRevision, revisionId]);

  const onToCounted = useCallback(async () => {
    setPrecheckError(null);
    setPrecheckMissingById({});

    // Pre-check before backend:
    // - all items must have counted_qty
    // - diff_reason is required only when expected_qty != counted_qty
    const missing = (items || [])
      .map((it) => {
        const problems = [];
        if (it.counted_qty === null || typeof it.counted_qty === "undefined") problems.push("Факт");
        if (isDiffReasonRequired(it.expected_qty, it.counted_qty, it.diff_qty) && !String(it.diff_reason || "").trim()) {
          problems.push("Причина");
        }
        return problems.length
          ? {
              id: it.id,
              product_name: it.product_name || null,
              product_barcode: it.product_barcode || null,
              problems,
            }
          : null;
      })
      .filter(Boolean);

    if (missing.length) {
      const map = {};
      for (const m of missing) {
        map[m.id] = {
          counted_qty: m.problems.includes("Факт"),
          diff_reason: m.problems.includes("Причина"),
        };
      }
      setPrecheckMissingById(map);
      setPrecheckError(`Не все позиции заполнены (${missing.length}). Смотри подсветку в таблице ниже.`);
      return;
    }

    try {
      await dispatch(toCountedInventoryRevision({ revisionId })).unwrap();
      fetchRevision();
      fetchItems();
    } catch {
      // error is in redux
    }
  }, [dispatch, fetchItems, fetchRevision, items, revisionId]);

  const onToApproved = useCallback(async () => {
    try {
      await dispatch(toApprovedInventoryRevision({ revisionId })).unwrap();
      fetchRevision();
      fetchItems();
    } catch {
      // error is in redux
    }
  }, [dispatch, fetchItems, fetchRevision, revisionId]);

  const onPublish = useCallback(async () => {
    try {
      await dispatch(publishInventoryRevision({ revisionId })).unwrap();
      fetchRevision();
      fetchItems();
    } catch {
      // error is in redux
    }
  }, [dispatch, fetchItems, fetchRevision, revisionId]);

  const onCancelRevision = useCallback(async () => {
    try {
      await dispatch(cancelInventoryRevision({ revisionId })).unwrap();
      fetchRevision();
      fetchItems();
    } catch {
      // error is in redux
    }
  }, [dispatch, fetchItems, fetchRevision, revisionId]);

  const onSearchProduct = useCallback(async () => {
    if (!normalizedBarcode) return;
    const barcodeToSearch = normalizedBarcode;
    setLastSearchedBarcode(barcodeToSearch);
    setBarcodeInput("");
    setLookupNotice(null);
    setLookupMode("revision_items");
    const res = await dispatch(fetchRevisionItemsByBarcode({ revisionId, barcode: barcodeToSearch }));
    const itemsFound = res?.payload?.items;
    const list = Array.isArray(itemsFound) ? itemsFound : [];
    if (list.length > 0) {
      const next = {};
      for (const it of list) {
        const countedPrefill = parseCountedInt(it?.counted_qty);
        next[it.id] = {
          counted_qty: countedPrefill === null ? it.counted_qty ?? "" : String(countedPrefill),
          diff_reason: it.diff_reason ?? "",
          diff_comment: it.diff_comment ?? "",
          error: null,
          fieldErrors: {},
          saving: false,
        };
      }
      setRevItemEdits(next);
      setLookupMode("revision_items");
      return;
    }

    setLookupMode("product");
    setProductFromBarcodeDraft({ counted_qty: "", diff_reason: "", diff_comment: "", saving: false, error: null, fieldErrors: {} });
    const p = await dispatch(fetchProductByBarcode({ barcode: barcodeToSearch }));
    if (p?.meta?.requestStatus === "rejected") {
      setLookupNotice("Товар не найден по этому штрихкоду");
    }
  }, [dispatch, normalizedBarcode, revisionId]);

  // Автопоиск по штрихкоду: 12 символов — ждём 800 мс, 13 символов — запрос сразу. Не вызывать, если модалку только что открыли по "ничего не найдено" или Enter в таблице — там уже сделан поиск.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!addProductOpen) return;
    if (!normalizedBarcode) {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      return;
    }
    if (skipNextDebounceSearch.current) {
      skipNextDebounceSearch.current = false;
      return;
    }
    const len = normalizedBarcode.length;
    if (len === 13) {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      onSearchProduct();
      return;
    }
    if (len === 12) {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        debounceRef.current = null;
        onSearchProduct();
      }, 800);
    } else {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    }
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [addProductOpen, normalizedBarcode, onSearchProduct]);

  const onRefreshRevisionItemLookup = useCallback(async () => {
    if (!barcodeForLookup) return;
    await dispatch(fetchRevisionItemsByBarcode({ revisionId, barcode: barcodeForLookup }));
  }, [barcodeForLookup, dispatch, revisionId]);

  const onCancelRevisionItemEdits = useCallback(() => {
    setRevItemEdits({});
  }, []);

  const saveProductFromBarcode = useCallback(async () => {
    if (!productLookup?.id) return;
    const expectedQty = 0;
    const countedInt = parseCountedInt(productFromBarcodeDraft.counted_qty);
    if (String(productFromBarcodeDraft.counted_qty || "").trim() === "") {
      setProductFromBarcodeDraft((prev) => ({
        ...prev,
        fieldErrors: { ...(prev.fieldErrors || {}), counted_qty: "Обязательное поле" },
      }));
      return;
    }
    if (productFromBarcodeDraft.counted_qty !== "" && countedInt === null) {
      setProductFromBarcodeDraft((prev) => ({
        ...prev,
        fieldErrors: { ...(prev.fieldErrors || {}), counted_qty: "Только целое число" },
      }));
      return;
    }
    const diffQty = countedInt !== null ? countedInt - expectedQty : null;
    const needReason = isDiffReasonRequired(expectedQty, countedInt, diffQty);
    if (needReason && !String(productFromBarcodeDraft.diff_reason || "").trim()) {
      setProductFromBarcodeDraft((prev) => ({
        ...prev,
        fieldErrors: { ...(prev.fieldErrors || {}), diff_reason: "Обязательное поле" },
      }));
      return;
    }
    setProductFromBarcodeDraft((prev) => ({ ...prev, saving: true, error: null, fieldErrors: {} }));
    try {
      await dispatch(
        createInventoryRevisionItem({
          revisionId,
          product: productLookup.id,
          expected_qty: "0",
          counted_qty: String(countedInt ?? ""),
          diff_reason: needReason ? String(productFromBarcodeDraft.diff_reason || "").trim() : "",
          diff_comment: String(productFromBarcodeDraft.diff_comment || ""),
        })
      ).unwrap();
      fetchItems();
      // Очищаем поле поиска и сбрасываем форму, модалка остаётся открытой для следующего штрихкода
      setBarcodeInput("");
      setLastSearchedBarcode("");
      setProductFromBarcodeDraft({ counted_qty: "", diff_reason: "", diff_comment: "", saving: false, error: null, fieldErrors: {} });
      setLookupMode("idle");
      setLookupNotice(null);
    } catch (e) {
      const fieldErrors = {};
      let general = null;
      if (e && typeof e === "object" && !Array.isArray(e)) {
        for (const [k, v] of Object.entries(e)) {
          if (k === "detail") {
            general = typeof v === "string" ? v : JSON.stringify(v);
            continue;
          }
          if (Array.isArray(v)) fieldErrors[k] = v.filter(Boolean).join(" ");
          else if (typeof v === "string") fieldErrors[k] = v;
        }
      }
      setProductFromBarcodeDraft((prev) => ({
        ...prev,
        saving: false,
        error: general || (Object.keys(fieldErrors).length ? null : "Failed to create item"),
        fieldErrors,
      }));
    }
  }, [dispatch, fetchItems, productFromBarcodeDraft, productLookup, revisionId]);

  const updateRevItemEdit = useCallback((itemId, field, value) => {
    setRevItemEdits((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [field]: value, error: null, fieldErrors: {} },
    }));
  }, []);

  const saveRevisionItemFromModal = useCallback(
    async (itemId) => {
      const draft = revItemEdits[itemId];
      if (!draft) return;
      const expected = Array.isArray(revisionItemsLookup)
        ? revisionItemsLookup.find((x) => x?.id === itemId)?.expected_qty
        : null;
      const countedInt = parseCountedInt(draft.counted_qty);
      if (draft.counted_qty !== "" && countedInt === null) {
        setRevItemEdits((prev) => ({
          ...prev,
          [itemId]: { ...prev[itemId], fieldErrors: { ...(prev[itemId]?.fieldErrors || {}), counted_qty: "Только целое число" } },
        }));
        return;
      }

      const expectedItem = Array.isArray(revisionItemsLookup)
        ? revisionItemsLookup.find((x) => x?.id === itemId) || null
        : null;
      const needReason = isDiffReasonRequired(expected, countedInt, expectedItem?.diff_qty);
      if (needReason && !String(draft.diff_reason || "").trim()) {
        setRevItemEdits((prev) => ({
          ...prev,
          [itemId]: { ...prev[itemId], fieldErrors: { ...(prev[itemId]?.fieldErrors || {}), diff_reason: "Обязательное поле" } },
        }));
        return;
      }
      setRevItemEdits((prev) => ({ ...prev, [itemId]: { ...prev[itemId], saving: true, error: null } }));
      try {
        await dispatch(
          patchInventoryRevisionItem({
            revisionId,
            itemId,
            counted_qty: draft.counted_qty === "" ? null : parseCountedInt(draft.counted_qty),
            diff_reason: needReason ? String(draft.diff_reason || "").trim() : null,
            diff_comment: draft.diff_comment === "" ? "" : draft.diff_comment,
          })
        ).unwrap();
        setRevItemEdits((prev) => ({ ...prev, [itemId]: { ...prev[itemId], saving: false } }));
        // refresh both lookup and main table
        await dispatch(fetchRevisionItemsByBarcode({ revisionId, barcode: barcodeForLookup }));
        fetchItems();

        // UX: close modal after Save (requested)
        setAddProductOpen(false);
        setBarcodeInput("");
        setLastSearchedBarcode("");
        setLookupMode("idle");
        setLookupNotice(null);
        setRevItemEdits({});
      } catch (e) {
        const fieldErrors = {};
        let general = null;
        if (e && typeof e === "object" && !Array.isArray(e)) {
          for (const [k, v] of Object.entries(e)) {
            if (k === "detail") {
              general = typeof v === "string" ? v : JSON.stringify(v);
              continue;
            }
            if (Array.isArray(v)) fieldErrors[k] = v.filter(Boolean).join(" ");
            else if (typeof v === "string") fieldErrors[k] = v;
          }
        }
        setRevItemEdits((prev) => ({
          ...prev,
          [itemId]: {
            ...prev[itemId],
            saving: false,
            error: general || (Object.keys(fieldErrors).length ? null : "Failed to save"),
            fieldErrors,
          },
        }));
      }
    },
    [barcodeForLookup, dispatch, fetchItems, revItemEdits, revisionId, setAddProductOpen, setBarcodeInput, setLookupMode, setLookupNotice]
  );

  const openEditModal = useCallback((it) => {
    setEditItemId(it?.id ?? null);
    const countedPrefill = parseCountedInt(it?.counted_qty);
    setEditDraft({
      counted_qty: countedPrefill === null ? it?.counted_qty ?? "" : String(countedPrefill),
      diff_reason: it?.diff_reason ?? "",
      diff_comment: it?.diff_comment ?? "",
      saving: false,
      error: null,
      fieldErrors: {},
    });
  }, []);

  const closeEditModal = useCallback(() => {
    setEditItemId(null);
    setEditDraft(null);
  }, []);

  const setEditDraftField = useCallback((field, value) => {
    setEditDraft((prev) => ({
      ...(prev || { counted_qty: "", diff_reason: "", diff_comment: "", saving: false, error: null, fieldErrors: {} }),
      [field]: value,
      error: null,
      fieldErrors: {},
    }));
  }, []);

  const saveEditModal = useCallback(async () => {
    if (!editItemId || !editDraft) return;
    const it = (items || []).find((x) => x?.id === editItemId) || null;
    const countedInt = parseCountedInt(editDraft.counted_qty);
    const needReason = isDiffReasonRequired(it?.expected_qty, countedInt, it?.diff_qty);

    // Keep logic consistent with InventoryRevisionItem modal:
    // counted_qty can be empty (null), but if filled it must be an integer.
    if (editDraft.counted_qty !== "" && countedInt === null) {
      setEditDraft((prev) => ({ ...prev, fieldErrors: { counted_qty: "Только целое число" } }));
      return;
    }

    if (needReason && !String(editDraft.diff_reason || "").trim()) {
      setEditDraft((prev) => ({ ...prev, fieldErrors: { diff_reason: "Обязательное поле" } }));
      return;
    }

    setEditDraft((prev) => ({ ...prev, saving: true, error: null }));
    try {
      await dispatch(
        patchInventoryRevisionItem({
          revisionId,
          itemId: editItemId,
          counted_qty: editDraft.counted_qty === "" ? null : parseCountedInt(editDraft.counted_qty),
          diff_reason: needReason ? String(editDraft.diff_reason || "").trim() : null,
          diff_comment: editDraft.diff_comment === "" ? "" : editDraft.diff_comment,
        })
      ).unwrap();
      closeEditModal();
      fetchItems();
    } catch (e) {
      const fieldErrors = {};
      let general = null;
      if (e && typeof e === "object" && !Array.isArray(e)) {
        for (const [k, v] of Object.entries(e)) {
          if (k === "detail") {
            general = typeof v === "string" ? v : JSON.stringify(v);
            continue;
          }
          if (Array.isArray(v)) fieldErrors[k] = v.filter(Boolean).join(" ");
          else if (typeof v === "string") fieldErrors[k] = v;
          else if (v != null) fieldErrors[k] = JSON.stringify(v);
        }
      } else if (typeof e === "string") {
        general = e;
      }
      setEditDraft((prev) => ({
        ...prev,
        saving: false,
        error: general || (Object.keys(fieldErrors).length ? null : "Failed to save"),
        fieldErrors,
      }));
    }
  }, [closeEditModal, dispatch, editDraft, editItemId, fetchItems, items, revisionId]);

  useEffect(() => {
    fetchRevision();
  }, [fetchRevision]);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchItems, applyKey]);

  useEffect(() => {
    if (itemsStatus === "succeeded" && clearFiltersAfterNextSuccess.current) {
      clearFiltersAfterNextSuccess.current = false;
      setBarcode("");
      setProductName("");
    }
  }, [itemsStatus]);

  // Когда в таблице "Ничего не найдено" и в фильтре введён штрихкод — запрос на /api/product/products/search-by-barcode/ и показ в модалке
  useEffect(() => {
    if (!normalizedFilterBarcode) {
      lastEmptyBarcodeTriggered.current = null;
      return;
    }
    if (itemsStatus !== "succeeded" || (items || []).length !== 0) return;
    if (lastEmptyBarcodeTriggered.current === normalizedFilterBarcode) return;
    lastEmptyBarcodeTriggered.current = normalizedFilterBarcode;
    skipNextDebounceSearch.current = true; // уже делаем поиск здесь — не дублировать в debounce
    setBarcodeInput(normalizedFilterBarcode);
    setLookupMode("product");
    setProductFromBarcodeDraft({ counted_qty: "", diff_reason: "", diff_comment: "", saving: false, error: null, fieldErrors: {} });
    setAddProductOpen(true);
    dispatch(fetchProductByBarcode({ barcode: normalizedFilterBarcode }));
  }, [itemsStatus, items, normalizedFilterBarcode, dispatch]);

  useEffect(() => {
    // Close edit modal if item disappeared
    if (editItemId != null && !(items || []).some((x) => x?.id === editItemId)) {
      closeEditModal();
    }
    // Any refresh should clear precheck highlights (they'll reappear on next "Подсчитано" click)
    setPrecheckMissingById({});
    setPrecheckError(null);
  }, [closeEditModal, editItemId, items]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.breadcrumbs}>
            <Link to={fromList || "/warehouse/revision"} style={styles.link}>
              Ревизии
            </Link>
            <span style={styles.sep}>/</span>
            <span style={styles.muted}>Ревизия #{revisionId}</span>
          </div>
          <div style={styles.title}>Ревизия #{revisionId}</div>
          <div style={styles.subTitle}>Позиции ревизии и поиск по штрихкоду/названию.</div>
        </div>

        <div style={styles.headerActions}>
          {revision?.status === "draft" && (
            <button
              type="button"
              onClick={onStartRevision}
              style={styles.btnPrimary}
              disabled={startStatus === "saving"}
              title="Перевести ревизию в статус 'В процессе' и создать позиции"
            >
              {startStatus === "saving" ? "Запуск…" : "Начать ревизию"}
            </button>
          )}
          {revision?.status === "in_progress" && (
            <button
              type="button"
              onClick={onToCounted}
              style={styles.btnInfo}
              disabled={toCountedStatus === "saving"}
              title="Проверить заполнение позиций и перевести ревизию в статус 'Подсчитано'"
            >
              {toCountedStatus === "saving" ? "Проверка…" : "Подсчитано"}
            </button>
          )}
          {revision?.status === "counted" && (
            <button
              type="button"
              onClick={onToApproved}
              style={styles.btnPrimary}
              disabled={toApprovedStatus === "saving"}
              title="Утвердить ревизию (перевести в статус 'Утверждено')"
            >
              {toApprovedStatus === "saving" ? "Утверждение…" : "Утвердить"}
            </button>
          )}
          {revision?.status === "approved" && (
            <button
              type="button"
              onClick={onPublish}
              style={styles.btnPrimary}
              disabled={publishStatus === "saving"}
              title="Опубликовать ревизию (перевести в статус 'Опубликовано')"
            >
              {publishStatus === "saving" ? "Публикация…" : "Опубликовать"}
            </button>
          )}
          {!!revision?.status && revision?.status !== "posted" && (
            <button
              type="button"
              onClick={() => setCancelConfirmOpen(true)}
              style={styles.btnGhost}
              disabled={cancelStatus === "saving"}
              title="Отменить ревизию"
            >
              {cancelStatus === "saving" ? "Отмена…" : "Отменить"}
            </button>
          )}
          <ConfirmModal
            open={cancelConfirmOpen}
            title="Отменить ревизию?"
            message="Ревизия будет переведена в статус «Отменена». Это действие нельзя вернуть."
            confirmLabel="Отменить ревизию"
            cancelLabel="Назад"
            danger
            onConfirm={() => {
              setCancelConfirmOpen(false);
              onCancelRevision();
            }}
            onCancel={() => setCancelConfirmOpen(false)}
          />
          <button type="button" onClick={fetchRevision} style={styles.btn}>
            Обновить ревизию
          </button>
          {revision && (
            <>
              <button
                type="button"
                onClick={onExportExcel}
                style={styles.btnGhost}
                disabled={exportFormat !== null}
                title="Скачать Excel: позиции, сводка, итоги; для опубликованных — лист «Финансовый итог» (недостача/излишек, суммы по себестоимости и опту, график)"
              >
                {exportFormat === "excel" ? "Excel…" : "Excel"}
              </button>
              <button
                type="button"
                onClick={onExportPdf}
                style={styles.btnGhost}
                disabled={exportFormat !== null}
                title="Скачать PDF: таблица позиций; для опубликованных — отдельная страница с финансовым итогом и диаграммой"
              >
                {exportFormat === "pdf" ? "PDF…" : "PDF"}
              </button>
            </>
          )}
          {(() => {
            const s = String(revision?.status || "").trim();
            const blocked = ["counted", "approved", "approvved", "posted", "cancelled"].includes(s);
            if (blocked) return null;
            return (
              <Link to={`/warehouse/revision/${revisionId}/edit`} state={{ from: fromList }} style={styles.btnGhostLink}>
                Редактировать
              </Link>
            );
          })()}
        </div>
      </div>

      {exportError && <div style={styles.exportError}>{exportError}</div>}

      {revStatus === "loading" && <Loading text="Загрузка ревизии…" />}
      {precheckError && (
        <div style={styles.error}>
          <div style={styles.errorTitle}>(предпроверка)</div>
          <div>{precheckError}</div>
        </div>
      )}
      {(revStatus === "failed" ||
        startStatus === "failed" ||
        toCountedStatus === "failed" ||
        toApprovedStatus === "failed" ||
        publishStatus === "failed" ||
        cancelStatus === "failed") && (
        <div style={styles.error}>{revError || startError || toCountedError || toApprovedError || publishError || cancelError}</div>
      )}

      {revision && (
        <div style={styles.meta}>
          <div style={styles.metaRow}>
            <div style={styles.metaLabel}>Магазин</div>
            <div style={styles.metaValue}>{revision.store_name || `Store #${revision.store}` || "—"}</div>
          </div>
          <div style={styles.metaRow}>
            <div style={styles.metaLabel}>Статус</div>
            <div style={styles.metaValue}>{inventoryRevisionStatusLabel(revision.status)}</div>
          </div>
          <div style={styles.metaRow}>
            <div style={styles.metaLabel}>Просрочено</div>
            <div style={styles.metaValue}>
              {revision.is_overdue ? <span style={styles.overdue}>Да</span> : <span style={styles.ok}>Нет</span>}
            </div>
          </div>
          <div style={styles.metaRow}>
            <div style={styles.metaLabel}>План. завершение</div>
            <div style={styles.metaValue}>{formatDateTime(revision.planned_finish_at)}</div>
          </div>
          <div style={styles.metaRow}>
            <div style={styles.metaLabel}>Создал</div>
            <div style={styles.metaValue}>{formatUser(revision.created_by)}</div>
          </div>
          <div style={styles.metaRow}>
            <div style={styles.metaLabel}>Посчитал</div>
            <div style={styles.metaValue}>{formatUser(revision.counted_by)}</div>
          </div>
          <div style={styles.metaRow}>
            <div style={styles.metaLabel}>Опубликовал</div>
            <div style={styles.metaValue}>{formatUser(revision.posted_by)}</div>
          </div>
          <div style={styles.metaRow}>
            <div style={styles.metaLabel}>Создано</div>
            <div style={styles.metaValue}>{formatDateTime(revision.created_at)}</div>
          </div>
          <div style={styles.metaRow}>
            <div style={styles.metaLabel}>Обновлено</div>
            <div style={styles.metaValue}>{formatDateTime(revision.updated_at)}</div>
          </div>
        </div>
      )}

      {revision?.status === "posted" && postedFinance && (
        <div style={styles.financeSection}>
          <div style={styles.financeTitle}>Финансы (опубликованная ревизия)</div>
          {financeScopeNote && <div style={styles.financeScopeNote}>{financeScopeNote}</div>}
          <div style={styles.financeKpis}>
            <div style={styles.financeKpi}>
              <div style={styles.financeKpiLabel}>Недостача — итого себестоимость</div>
              <div style={styles.financeKpiValue}>{moneyRu(postedFinance.tShort.cost)}</div>
            </div>
            <div style={styles.financeKpi}>
              <div style={styles.financeKpiLabel}>Недостача — итого розница</div>
              <div style={styles.financeKpiValue}>{moneyRu(postedFinance.tShort.retail)}</div>
            </div>
            <div style={styles.financeKpi}>
              <div style={styles.financeKpiLabel}>Излишек — итого себестоимость</div>
              <div style={styles.financeKpiValue}>{moneyRu(postedFinance.tSur.cost)}</div>
            </div>
            <div style={styles.financeKpi}>
              <div style={styles.financeKpiLabel}>Излишек — итого розница</div>
              <div style={styles.financeKpiValue}>{moneyRu(postedFinance.tSur.retail)}</div>
            </div>
            <div style={styles.financeKpi}>
              <div style={styles.financeKpiLabel}>Недостача, строк</div>
              <div style={styles.financeKpiValue}>{postedFinance.tShort.lines}</div>
            </div>
            <div style={styles.financeKpi}>
              <div style={styles.financeKpiLabel}>Излишек, строк</div>
              <div style={styles.financeKpiValue}>{postedFinance.tSur.lines}</div>
            </div>
          </div>
          <div style={styles.financeLinks}>
            <Link to={`/warehouse/revision/${revisionId}/finance/shortage`} style={styles.btnGhostLink}>
              Недостача (ожид. − факт {'>'} 0) — открыть таблицу
            </Link>
            <Link to={`/warehouse/revision/${revisionId}/finance/surplus`} style={styles.btnGhostLink}>
              Излишек (ожид. − факт {'<'} 0) — открыть таблицу
            </Link>
          </div>
        </div>
      )}

      <div style={styles.filters}>
        <input
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          placeholder="Штрихкод (barcode)"
          style={styles.input}
          onKeyDown={async (e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            const normalized = String(barcode || "").replace(/\s+/g, "").trim();
            if (!normalized) return;

            // UX: scan barcode -> open the same edit modal as clicking an InventoryRevisionItem row.
            const res = await dispatch(fetchRevisionItemsByBarcode({ revisionId, barcode: normalized }));
            const itemsFound = res?.payload?.items;
            const list = Array.isArray(itemsFound) ? itemsFound : [];

            if (list.length === 1) {
              openEditModal(list[0]);
              return;
            }

            // 0 or many -> open chooser/search modal prefilled with this barcode
            setLookupNotice(null);
            setRevItemEdits({});
            setBarcodeInput(normalized);
            setAddProductOpen(true);
            if (list.length === 0) {
              skipNextDebounceSearch.current = true; // поиск уже делаем здесь — не дублировать в debounce
              setLookupMode("product");
              setProductFromBarcodeDraft({ counted_qty: "", diff_reason: "", diff_comment: "", saving: false, error: null, fieldErrors: {} });
              dispatch(fetchProductByBarcode({ barcode: normalized }));
            } else {
              setLookupMode("revision_items");
            }
          }}
        />
        <input
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
          placeholder="Название товара (product_name)"
          style={styles.input}
        />
        <button
          type="button"
          onClick={() => {
            clearFiltersAfterNextSuccess.current = true;
            setApplyKey((v) => v + 1);
          }}
          style={styles.btn}
        >
          Поиск
        </button>
        <button
          type="button"
          onClick={() => {
            setBarcode("");
            setProductName("");
            setApplyKey((v) => v + 1);
          }}
          style={styles.btnGhost}
        >
          Сброс
        </button>
      </div>

      {itemsStatus === "loading" && <Loading text="Загрузка позиций…" />}
      {itemsStatus === "failed" && <div style={styles.error}>{itemsError}</div>}

      <div style={styles.body}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Товар</th>
                <th style={styles.th}>Штрихкод</th>
                <th style={styles.th}>Ожид.</th>
                <th style={styles.th}>Факт</th>
                <th style={styles.th}>Разн.</th>
                <th style={styles.th}>Причина</th>
                <th style={styles.th}>Комментарий</th>
                <th style={styles.th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && itemsStatus === "succeeded" ? (
                <tr>
                  <td colSpan={9} style={styles.tdMuted}>
                    Ничего не найдено
                  </td>
                </tr>
              ) : (
                items.map((it) => {
                  const notCounted = it.counted_qty === null || typeof it.counted_qty === "undefined";
                  const pre = precheckMissingById[it.id] || null;
                  const rowBaseStyle = pre ? styles.trError : notCounted ? styles.trWarn : styles.tr;
                  return (
                    <tr
                      key={it.id}
                      style={{ ...rowBaseStyle, ...styles.trClickable }}
                      onClick={() => openEditModal(it)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") openEditModal(it);
                      }}
                      title="Открыть для обновления"
                    >
                      <td style={styles.tdMono}>{it.id}</td>
                      <td style={styles.td}>{it.product_name || it.product || "—"}</td>
                      <td style={styles.tdMono}>{it.product_barcode || "—"}</td>
                      <td style={styles.td}>{formatQty(it.expected_qty)}</td>
                      <td style={styles.td}>
                        {notCounted ? (
                          <span style={styles.missing}>—</span>
                        ) : (
                          formatInt(it.counted_qty)
                        )}
                        {pre?.counted_qty && <div style={styles.fieldError}>Обязательное поле</div>}
                      </td>
                      <td style={styles.td}>
                        {(() => {
                          const d = getDisplayDiff(it.expected_qty, it.counted_qty, it.diff_qty);
                          return d !== null ? formatQty(d) : "—";
                        })()}
                      </td>
                      <td style={styles.td}>
                        <span>{inventoryDiffReasonLabel(it.diff_reason)}</span>
                        {pre?.diff_reason && <div style={styles.fieldError}>Обязательное поле</div>}
                      </td>
                      <td style={styles.td}>
                        {it.diff_comment || "—"}
                      </td>
                      <td style={styles.td}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(it);
                          }}
                          style={styles.btnSmall}
                          title="Открыть модальное окно для обновления"
                        >
                          Обновить
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editItemId != null && editDraft && (
        <div style={styles.modalOverlay} onMouseDown={closeEditModal}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Обновить позицию #{editItemId}</div>
              <button type="button" onClick={closeEditModal} style={styles.modalClose}>
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              {(() => {
                const it = (items || []).find((x) => x?.id === editItemId) || null;
                const expectedQty = it?.expected_qty ?? null;
                const countedInt = parseCountedInt(editDraft.counted_qty);
                const needReason = isDiffReasonRequired(expectedQty, countedInt, it?.diff_qty);
                const effectiveCounted = countedInt !== null ? countedInt : parseQty(it?.counted_qty);
                const d = getDisplayDiff(expectedQty, effectiveCounted, it?.diff_qty);
                const diffPillStyle =
                  d === null
                    ? styles.qtyPillNeutral
                    : d > 0
                      ? styles.qtyPillPositive
                      : d < 0
                        ? styles.qtyPillNegative
                        : styles.qtyPillNeutral;
                return (
                  <>
                    <div style={styles.productCard}>
                      <div style={styles.revItemTopRow}>
                        <div style={{ display: "grid", gap: 4 }}>
                          <div style={styles.productTitleSmall}>{it?.product_name || it?.product || "—"}</div>
                          <div style={styles.productMeta}>
                            <span style={styles.productMetaLabel}>ID</span>{" "}
                            <span style={{ ...styles.mono, fontSize: 15 }}>{editItemId}</span>
                          </div>
                        </div>
                        <BarcodeBlock barcode={it?.product_barcode} />
                      </div>
                    </div>

                    <div style={styles.modalFieldsRow}>
                      <div style={styles.modalField}>
                        <div style={styles.modalFieldLabel}>Ожидаемое (expected_qty)</div>
                        <input value={formatQty(expectedQty)} readOnly style={styles.cellInputReadOnly} />
                      </div>
                      <div style={styles.modalField}>
                        <div style={styles.modalFieldLabel}>Разница (diff_qty)</div>
                        <div style={{ ...styles.cellPill, ...diffPillStyle }}>{formatSignedQty(d)}</div>
                      </div>
                      <div style={styles.modalField}>
                        <div style={styles.modalFieldLabel}>
                          Факт (counted_qty)
                        </div>
                        <input
                          type="number"
                          step="1"
                          inputMode="numeric"
                          value={editDraft.counted_qty}
                          onChange={(e) => setEditDraftField("counted_qty", e.target.value)}
                          style={styles.cellInputWide}
                        />
                        {editDraft.fieldErrors?.counted_qty && <div style={styles.fieldError}>{editDraft.fieldErrors.counted_qty}</div>}
                      </div>

                      <div style={styles.modalField}>
                        <div style={styles.modalFieldLabel}>
                          Причина (diff_reason){" "}
                          {needReason ? <span style={styles.req}>*</span> : <span style={styles.badgeOptional}>не обязательно</span>}
                        </div>
                        <Select
                          value={editDraft.diff_reason}
                          onChange={(v) => setEditDraftField("diff_reason", v)}
                          options={[
                            { value: "", label: "—" },
                            ...INVENTORY_DIFF_REASON_OPTIONS,
                          ]}
                        />
                        {needReason && editDraft.fieldErrors?.diff_reason && (
                          <div style={styles.fieldError}>{editDraft.fieldErrors.diff_reason}</div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                      <div style={{ fontSize: 14, fontWeight: 900, opacity: 0.75 }}>Комментарий (diff_comment)</div>
                      <textarea
                        value={editDraft.diff_comment}
                        onChange={(e) => setEditDraftField("diff_comment", e.target.value)}
                        style={styles.textarea}
                        rows={3}
                      />
                      {editDraft.fieldErrors?.diff_comment && <div style={styles.fieldError}>{editDraft.fieldErrors.diff_comment}</div>}
                    </div>

                    {editDraft.error && <div style={{ marginTop: 10, ...styles.fieldError }}>{editDraft.error}</div>}

                    <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                      <button type="button" onClick={saveEditModal} style={styles.btnSmallPrimary} disabled={editDraft.saving}>
                        {editDraft.saving ? "Сохранение…" : "Сохранить"}
                      </button>
                      <button type="button" onClick={closeEditModal} style={styles.btnSmallGhost}>
                        Отмена
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {addProductOpen && (
        <div style={styles.modalOverlay} onMouseDown={() => { setAddProductOpen(false); setBarcodeInput(""); setLastSearchedBarcode(""); }}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Добавить товар</div>
              <button type="button" onClick={() => { setAddProductOpen(false); setBarcodeInput(""); setLastSearchedBarcode(""); }} style={styles.modalClose}>
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.fieldRow}>
                <input
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onFocus={(e) => {
                    // UX: fast rescan / edit
                    e.target.select();
                    setLookupNotice(null);
                  }}
                  autoFocus
                  placeholder="Штрихкод…"
                  style={styles.modalBarcodeInput}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onSearchProduct();
                  }}
                />
              </div>

              {lookupNotice && <div style={styles.error}>{lookupNotice}</div>}
              {revisionItemsLookupStatus === "failed" && <div style={styles.error}>{revisionItemsLookupError}</div>}

              {Array.isArray(revisionItemsLookup) && revisionItemsLookup.length > 0 && (
                <div style={styles.productCard}>
                  <div style={styles.productTitle}>Позиция ревизии</div>
                  <div style={styles.productMeta}>
                    Найдено позиций: <b>{revisionItemsLookup.length}</b>
                  </div>

                  <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                    {revisionItemsLookup.map((it) => {
                      const countedPrefill = parseCountedInt(it?.counted_qty);
                      const ed = revItemEdits[it.id] || {
                        counted_qty: countedPrefill === null ? it.counted_qty ?? "" : String(countedPrefill),
                        diff_reason: it.diff_reason ?? "",
                        diff_comment: it.diff_comment ?? "",
                        fieldErrors: {},
                        error: null,
                        saving: false,
                      };
                      const countedInt = parseCountedInt(ed.counted_qty);
                      const needReason = isDiffReasonRequired(it.expected_qty, countedInt, it?.diff_qty);
                      const effectiveCounted = countedInt !== null ? countedInt : parseQty(it?.counted_qty);
                      const d = getDisplayDiff(it.expected_qty, effectiveCounted, it?.diff_qty);
                      const diffPillStyle =
                        d === null
                          ? styles.qtyPillNeutral
                          : d > 0
                            ? styles.qtyPillPositive
                            : d < 0
                              ? styles.qtyPillNegative
                              : styles.qtyPillNeutral;
                      return (
                        <div key={it.id} style={styles.revItemCard}>
                          <div style={styles.revItemTopRow}>
                            <div style={{ display: "grid", gap: 4 }}>
                              <div style={styles.productTitleSmall}>{it.product_name || it.product || "—"}</div>
                              <div style={styles.productMeta}>
                                <span style={styles.productMetaLabel}>ID</span> <span style={{ ...styles.mono, fontSize: 15 }}>{it.id}</span>{" "}
                              </div>
                            </div>
                            <BarcodeBlock barcode={it.product_barcode} />
                          </div>

                          <div style={styles.modalFieldsRow}>
                            <div style={styles.modalField}>
                              <div style={styles.modalFieldLabel}>Ожидаемое (expected_qty)</div>
                              <input value={formatQty(it.expected_qty)} readOnly style={styles.cellInputReadOnly} />
                            </div>
                            <div style={styles.modalField}>
                              <div style={styles.modalFieldLabel}>Разница (diff_qty)</div>
                              <div style={{ ...styles.cellPill, ...diffPillStyle }}>{formatSignedQty(d)}</div>
                            </div>
                            <div style={styles.modalField}>
                              <div style={styles.modalFieldLabel}>Факт (counted_qty)</div>
                              <input
                                type="number"
                                step="1"
                                inputMode="numeric"
                                value={ed.counted_qty}
                                onChange={(e) => updateRevItemEdit(it.id, "counted_qty", e.target.value)}
                                style={styles.cellInputWide}
                              />
                              {ed.fieldErrors?.counted_qty && (
                                <div style={styles.fieldError}>{ed.fieldErrors.counted_qty}</div>
                              )}
                            </div>

                            <div style={styles.modalField}>
                              <div style={styles.modalFieldLabel}>
                                Причина (diff_reason){" "}
                                {needReason ? <span style={styles.req}>*</span> : <span style={styles.badgeOptional}>не обязательно</span>}
                              </div>
                              <Select
                                value={ed.diff_reason}
                                onChange={(v) => updateRevItemEdit(it.id, "diff_reason", v)}
                                options={[
                                  { value: "", label: "—" },
                                  ...INVENTORY_DIFF_REASON_OPTIONS,
                                ]}
                              />
                              {needReason && ed.fieldErrors?.diff_reason && <div style={styles.fieldError}>{ed.fieldErrors.diff_reason}</div>}
                            </div>
                          </div>

                          <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                            <div style={{ fontSize: 14, fontWeight: 900, opacity: 0.75 }}>Комментарий (diff_comment)</div>
                            <textarea
                              value={ed.diff_comment}
                              onChange={(e) => updateRevItemEdit(it.id, "diff_comment", e.target.value)}
                              style={styles.textarea}
                              rows={3}
                            />
                            {ed.fieldErrors?.diff_comment && <div style={styles.fieldError}>{ed.fieldErrors.diff_comment}</div>}
                          </div>

                          {ed.error && <div style={{ marginTop: 10, ...styles.fieldError }}>{ed.error}</div>}

                          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                            <button type="button" onClick={() => saveRevisionItemFromModal(it.id)} style={styles.btnSmallPrimary} disabled={ed.saving}>
                              {ed.saving ? "Сохранение…" : "Сохранить"}
                            </button>
                            <button type="button" onClick={onCancelRevisionItemEdits} style={styles.btnSmallGhost}>
                              Отмена
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {revisionItemsLookupStatus === "loading" && <div style={styles.notice}>Поиск в ревизии…</div>}

              {Array.isArray(revisionItemsLookup) &&
                revisionItemsLookup.length === 0 &&
                lookupMode === "product" &&
                productLookupStatus === "loading" && <div style={styles.notice}>Поиск товара…</div>}

              {Array.isArray(revisionItemsLookup) && revisionItemsLookup.length === 0 && lookupMode === "product" && productLookupStatus === "failed" && (
                <div style={styles.error}>{productLookupError}</div>
              )}

              {Array.isArray(revisionItemsLookup) &&
                revisionItemsLookup.length === 0 &&
                lookupMode === "product" &&
                productLookup &&
                (() => {
                  const expectedQty = 0;
                  const countedInt = parseCountedInt(productFromBarcodeDraft.counted_qty);
                  const diffQty = countedInt !== null ? countedInt - expectedQty : null;
                  const needReason = isDiffReasonRequired(expectedQty, countedInt, diffQty);
                  const diffPillStyle =
                    diffQty === null
                      ? styles.qtyPillNeutral
                      : diffQty > 0
                        ? styles.qtyPillPositive
                        : diffQty < 0
                          ? styles.qtyPillNegative
                          : styles.qtyPillNeutral;
                  return (
                    <div style={styles.productCard}>
                      <div style={styles.revItemTopRow}>
                        <div style={{ display: "grid", gap: 4 }}>
                          <div style={styles.productTitleSmall}>{productLookup.name || "—"}</div>
                          <div style={styles.productMeta}>
                            <span style={styles.productMetaLabel}>ID</span>{" "}
                            <span style={{ ...styles.mono, fontSize: 15 }}>{productLookup.id ?? "—"}</span>
                          </div>
                        </div>
                        <BarcodeBlock barcode={productLookup.barcode} />
                      </div>
                      <div style={styles.modalFieldsRow}>
                        <div style={styles.modalField}>
                          <div style={styles.modalFieldLabel}>Ожидаемое (expected_qty)</div>
                          <input value="0" readOnly style={styles.cellInputReadOnly} />
                        </div>
                        <div style={styles.modalField}>
                          <div style={styles.modalFieldLabel}>Разница (diff_qty)</div>
                          <div style={{ ...styles.cellPill, ...diffPillStyle }}>{formatSignedQty(diffQty)}</div>
                        </div>
                        <div style={styles.modalField}>
                          <div style={styles.modalFieldLabel}>Факт (counted_qty)</div>
                          <input
                            type="number"
                            step="1"
                            inputMode="numeric"
                            value={productFromBarcodeDraft.counted_qty}
                            onChange={(e) =>
                              setProductFromBarcodeDraft((prev) => ({
                                ...prev,
                                counted_qty: e.target.value,
                                error: null,
                                fieldErrors: {},
                              }))
                            }
                            style={styles.cellInputWide}
                          />
                          {productFromBarcodeDraft.fieldErrors?.counted_qty && (
                            <div style={styles.fieldError}>{productFromBarcodeDraft.fieldErrors.counted_qty}</div>
                          )}
                        </div>
                        <div style={styles.modalField}>
                          <div style={styles.modalFieldLabel}>
                            Причина (diff_reason){" "}
                            {needReason ? <span style={styles.req}>*</span> : <span style={styles.badgeOptional}>не обязательно</span>}
                          </div>
                          <Select
                            value={productFromBarcodeDraft.diff_reason}
                            onChange={(v) =>
                              setProductFromBarcodeDraft((prev) => ({
                                ...prev,
                                diff_reason: v,
                                error: null,
                                fieldErrors: {},
                              }))
                            }
                            options={[
                              { value: "", label: "—" },
                              ...INVENTORY_DIFF_REASON_OPTIONS,
                            ]}
                          />
                          {needReason && productFromBarcodeDraft.fieldErrors?.diff_reason && (
                            <div style={styles.fieldError}>{productFromBarcodeDraft.fieldErrors.diff_reason}</div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                        <div style={{ fontSize: 14, fontWeight: 900, opacity: 0.75 }}>Комментарий (diff_comment)</div>
                        <textarea
                          value={productFromBarcodeDraft.diff_comment}
                          onChange={(e) =>
                            setProductFromBarcodeDraft((prev) => ({
                              ...prev,
                              diff_comment: e.target.value,
                              error: null,
                              fieldErrors: {},
                            }))
                          }
                          style={styles.textarea}
                          rows={3}
                        />
                        {productFromBarcodeDraft.fieldErrors?.diff_comment && (
                          <div style={styles.fieldError}>{productFromBarcodeDraft.fieldErrors.diff_comment}</div>
                        )}
                      </div>
                      {productFromBarcodeDraft.error && (
                        <div style={{ marginTop: 10, ...styles.fieldError }}>{productFromBarcodeDraft.error}</div>
                      )}
                      <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={saveProductFromBarcode}
                          style={styles.btnSmallPrimary}
                          disabled={productFromBarcodeDraft.saving}
                        >
                          {productFromBarcodeDraft.saving ? "Сохранение…" : "Сохранить"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setProductFromBarcodeDraft({
                              counted_qty: "",
                              diff_reason: "",
                              diff_comment: "",
                              saving: false,
                              error: null,
                              fieldErrors: {},
                            });
                            setAddProductOpen(false);
                          }}
                          style={styles.btnSmallGhost}
                          disabled={productFromBarcodeDraft.saving}
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  );
                })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "radial-gradient(1200px 600px at 20% -10%, rgba(245,197,24,0.08), transparent 60%), var(--c-bg)",
    color: "var(--c-text)",
  },
  header: {
    padding: "16px 18px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  breadcrumbs: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, opacity: 0.9 },
  link: { color: "var(--c-s1)", textDecoration: "none", fontWeight: 900 },
  sep: { opacity: 0.5 },
  muted: { opacity: 0.7, fontWeight: 800 },
  title: { fontSize: 20, fontWeight: 900, marginTop: 6 },
  subTitle: { marginTop: 6, opacity: 0.75, fontSize: 14, lineHeight: 1.35, maxWidth: 760 },
  headerActions: { display: "flex", gap: 10, alignItems: "center" },
  btnPrimary: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid rgba(245,197,24,0.3)",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnInfo: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "rgba(245,197,24,0.12)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btn: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnGhost: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnGhostLink: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    textDecoration: "none",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
    display: "inline-flex",
    alignItems: "center",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 50,
  },
  modal: {
    width: "min(980px, 96vw)",
    background: "var(--c-bg)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid var(--c-border)",
  },
  modalTitle: { fontSize: 16, fontWeight: 900 },
  modalClose: {
    width: 34,
    height: 34,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 20,
    lineHeight: "30px",
  },
  modalBody: { padding: 16, display: "grid", gap: 14 },
  fieldRow: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  productCard: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    padding: 14,
  },
  revItemCard: {
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    borderRadius: 16,
    padding: 12,
  },
  productTitle: { fontSize: 18, fontWeight: 900, marginBottom: 8 },
  modalProductTitle: { fontSize: 23, fontWeight: 950, marginBottom: 10, textAlign: "center", lineHeight: 1.2 },
  productTitleSmall: { fontSize: 17, fontWeight: 900 },
  productMeta: { fontSize: 15, opacity: 0.9, lineHeight: 1.5 },
  productMetaLabel: { fontSize: 13, fontWeight: 900, opacity: 0.65, textTransform: "uppercase", letterSpacing: "0.04em" },
  productDesc: { marginTop: 10, fontSize: 15, opacity: 0.85, lineHeight: 1.45 },
  productTopRow: { display: "flex", gap: 14, alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap" },
  productTopRowCentered: { display: "flex", gap: 14, alignItems: "flex-end", justifyContent: "center", flexWrap: "wrap" },
  revItemTopRow: { display: "flex", gap: 14, alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap" },
  barcodeBlock: { display: "grid", gap: 6, justifyItems: "start" },
  barcodeVisual: {
    width: 240,
    maxWidth: "70vw",
    height: 44,
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
  },
  barcodePlaceholder: {
    width: "100%",
    height: "100%",
    background:
      "repeating-linear-gradient(90deg, rgba(245,197,24,0.12) 0 2px, rgba(11,18,32,1) 2px 4px, rgba(245,197,24,0.08) 4px 5px, rgba(11,18,32,1) 5px 7px)",
    opacity: 0.35,
  },
  barcodeText: {
    fontSize: 15,
    fontWeight: 900,
    opacity: 0.9,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    letterSpacing: "0.06em",
  },
  qtyPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "fit-content",
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid var(--c-border)",
    fontSize: 15,
    fontWeight: 900,
    letterSpacing: "0.02em",
  },
  qtyPillNeutral: { background: "var(--c-card)", color: "var(--c-text)" },
  qtyPillPositive: { background: "color-mix(in srgb, var(--c-success) 14%, transparent)", border: "1px solid rgba(34,197,94,0.26)", color: "var(--c-success)" },
  qtyPillNegative: { background: "rgba(239,68,68,0.14)", border: "1px solid rgba(239,68,68,0.26)", color: "var(--c-danger)" },
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  notice: { padding: "12px 18px", fontSize: 15, opacity: 0.85 },
  error: {
    padding: "12px 18px",
    fontSize: 15,
    background: "rgba(248,113,113,0.1)",
    borderTop: "1px solid rgba(248,113,113,0.2)",
    borderBottom: "1px solid rgba(248,113,113,0.2)",
  },
  exportError: {
    padding: "10px 18px",
    fontSize: 15,
    margin: "0 0 0 0",
    background: "rgba(248,113,113,0.1)",
    borderBottom: "1px solid rgba(248,113,113,0.2)",
  },
  errorTitle: { fontSize: 14, fontWeight: 900, marginBottom: 6, opacity: 0.95 },
  errorPre: {
    margin: 0,
    whiteSpace: "pre-wrap",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    fontSize: 14,
    lineHeight: 1.35,
  },
  meta: {
    padding: 18,
    display: "grid",
    gap: 10,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    borderBottom: "1px solid var(--c-border)",
  },
  metaRow: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 14,
    padding: 12,
    display: "grid",
    gap: 6,
  },
  metaLabel: { fontSize: 13, opacity: 0.65, fontWeight: 900 },
  metaValue: { fontSize: 15, fontWeight: 900, color: "var(--c-text)" },
  filters: {
    padding: 18,
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    borderBottom: "1px solid var(--c-border)",
  },
  input: {
    width: 280,
    maxWidth: "70vw",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
  },
  modalBarcodeInput: {
    width: "min(620px, 78vw)",
    maxWidth: "100%",
    padding: "14px 16px",
    height: 56,
    boxSizing: "border-box",
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 18,
    fontWeight: 800,
  },
  cellInput: {
    width: 140,
    maxWidth: "100%",
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
  },
  cellInputWide: {
    width: "100%",
    maxWidth: "100%",
    padding: "14px 14px",
    height: 52,
    boxSizing: "border-box",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 18,
  },
  cellInputReadOnly: {
    width: "100%",
    maxWidth: "100%",
    padding: "14px 14px",
    height: 52,
    boxSizing: "border-box",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 17,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  cellPill: {
    width: "100%",
    maxWidth: "100%",
    padding: "14px 14px",
    height: 52,
    boxSizing: "border-box",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    fontSize: 17,
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cellSelectWide: {
    width: "100%",
    maxWidth: "100%",
    padding: "14px 14px",
    height: 52,
    boxSizing: "border-box",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 17,
  },
  modalFieldsRow: {
    display: "grid",
    gap: 14,
    alignItems: "end",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  },
  modalField: { display: "grid", gap: 6 },
  modalFieldLabel: { fontSize: 15, fontWeight: 900, opacity: 0.75 },
  textarea: {
    width: "100%",
    minWidth: 260,
    padding: "12px 12px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 17,
    resize: "vertical",
  },
  badgeOptional: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 999,
    border: "1px solid rgba(245,197,24,0.25)",
    background: "rgba(245,197,24,0.08)",
    color: "var(--c-text)",
    fontSize: 13,
    fontWeight: 900,
    marginLeft: 8,
    whiteSpace: "nowrap",
  },
  cellSelect: {
    width: 180,
    maxWidth: "100%",
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
  },
  body: { padding: 18, minHeight: 0, overflow: "auto" },
  tableWrap: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 14,
    fontWeight: 900,
    color: "var(--c-text-muted)",
    background: "var(--c-input)",
    borderBottom: "1px solid var(--c-border)",
    whiteSpace: "nowrap",
  },
  req: { color: "var(--c-accent)", fontWeight: 900 },
  overdue: { color: "var(--c-danger)", fontWeight: 900 },
  ok: { color: "var(--c-text-sub)", fontWeight: 900 },
  tr: {},
  trClickable: {
    cursor: "pointer",
    transition: "background 120ms ease",
  },
  trError: { background: "rgba(251, 113, 133, 0.08)" },
  trWarn: { background: "rgba(245,197,24,0.05)" },
  td: {
    padding: "10px 12px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    verticalAlign: "middle",
  },
  tdMono: {
    padding: "10px 12px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  tdMuted: { padding: 14, fontSize: 15, opacity: 0.75 },
  missing: { color: "var(--c-text-muted)", fontWeight: 900 },
  rowActions: { display: "grid", gap: 8 },
  btnSmall: {
    padding: "8px 10px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 14,
    width: "fit-content",
  },
  btnSmallPrimary: {
    padding: "8px 10px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 14,
    width: "fit-content",
  },
  btnSmallGhost: {
    padding: "8px 10px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 14,
    width: "fit-content",
  },
  rowError: { fontSize: 14, color: "var(--c-danger)", fontWeight: 800, maxWidth: 240, lineHeight: 1.3 },
  fieldError: { marginTop: 6, fontSize: 14, color: "var(--c-danger)", fontWeight: 800, lineHeight: 1.2 },
  financeSection: {
    margin: "0 18px 16px",
    padding: 16,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
  },
  financeTitle: { fontSize: 17, fontWeight: 900, marginBottom: 10 },
  financeScopeNote: {
    fontSize: 14,
    fontWeight: 800,
    color: "var(--c-accent)",
    marginBottom: 12,
    lineHeight: 1.4,
    maxWidth: 900,
  },
  financeKpis: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 10,
    marginBottom: 14,
  },
  financeKpi: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
  },
  financeKpiLabel: { fontSize: 13, fontWeight: 800, color: "var(--c-text-sub)", marginBottom: 4 },
  financeKpiValue: { fontSize: 16, fontWeight: 900 },
  financeLinks: { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 4 },
};


